import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function EntropyChangeCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Entropy Change Calculator", url: "https://calclab.app/calculator/entropy-change" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Entropy Change Calculator",
      "Calculate entropy changes in thermodynamic processes. Free online calculator with ΔS = Q/T and ΔS = mc·ln(T₂/T₁) formulas.",
      "https://calclab.app/calculator/entropy-change",
      lang
    )
  ]);
  const [heat, setHeat] = useState("");
  const [temperature, setTemperature] = useState("");
  const [resultReversible, setResultReversible] = useState<number | null>(null);

  const [mass, setMass] = useState("");
  const [specificHeat, setSpecificHeat] = useState("");
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [resultTempChange, setResultTempChange] = useState<number | null>(null);

  const calculateReversible = () => {
    const q = parseFloat(heat);
    const t = parseFloat(temperature);

    if (isNaN(q) || isNaN(t) || t === 0) {
      toast.error("Please enter valid values (temperature must be non-zero)");
      return;
    }

    const ds = q / t;
    setResultReversible(ds);
    toast.success("Calculation complete!");
  };

  const calculateTempChange = () => {
    const m = parseFloat(mass);
    const c = parseFloat(specificHeat);
    const temp1 = parseFloat(t1);
    const temp2 = parseFloat(t2);

    if (isNaN(m) || isNaN(c) || isNaN(temp1) || isNaN(temp2) || temp1 === 0 || temp2 === 0) {
      toast.error("Please enter valid values");
      return;
    }

    const ds = m * c * Math.log(temp2 / temp1);
    setResultTempChange(ds);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setHeat("");
    setTemperature("");
    setMass("");
    setSpecificHeat("");
    setT1("");
    setT2("");
    setResultReversible(null);
    setResultTempChange(null);
  };

  return (
    <PageLayout 
      calculatorName="Entropy Change Calculator" 
      calculatorPath="/calculator/entropy-change"
      category="chemistry"
      subcategory="Thermochemistry"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Entropy Change Calculator</h1>
          <p className="text-muted-foreground">
            Calculate entropy changes in thermodynamic processes
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Entropy Formulas</CardTitle>
            <CardDescription>
              ΔS = Q/T (reversible) | ΔS = mc·ln(T₂/T₁) (temperature change)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="reversible" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="reversible">Reversible Process</TabsTrigger>
                <TabsTrigger value="tempchange">Temperature Change</TabsTrigger>
              </TabsList>

              <TabsContent value="reversible" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="heat">Heat Transferred Q (J)</Label>
                    <Input
                      id="heat"
                      type="number"
                      step="any"
                      placeholder="Enter heat"
                      value={heat}
                      onChange={(e) => setHeat(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="temperature">Temperature T (K)</Label>
                    <Input
                      id="temperature"
                      type="number"
                      step="any"
                      placeholder="Enter temperature"
                      value={temperature}
                      onChange={(e) => setTemperature(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateReversible} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {resultReversible !== null && (
                  <>
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                      <CardHeader>
                        <CardTitle className="text-xl">Result</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-primary">
                          {resultReversible.toFixed(3)} J/K
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Entropy Change
                        </p>
                      </CardContent>
                    </Card>
                    
                    <CalculatorSuperCalcCTA config={calculatorCTAs["entropy-change"]} trigger={resultReversible} />
                  </>
                )}
              </TabsContent>

              <TabsContent value="tempchange" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="mass">Mass (kg)</Label>
                    <Input
                      id="mass"
                      type="number"
                      step="any"
                      placeholder="Enter mass"
                      value={mass}
                      onChange={(e) => setMass(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="specificHeat">Specific Heat c (J/kg·K)</Label>
                    <Input
                      id="specificHeat"
                      type="number"
                      step="any"
                      placeholder="Water: 4186"
                      value={specificHeat}
                      onChange={(e) => setSpecificHeat(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="t1">Initial Temperature T₁ (K)</Label>
                    <Input
                      id="t1"
                      type="number"
                      step="any"
                      placeholder="Enter initial temperature"
                      value={t1}
                      onChange={(e) => setT1(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="t2">Final Temperature T₂ (K)</Label>
                    <Input
                      id="t2"
                      type="number"
                      step="any"
                      placeholder="Enter final temperature"
                      value={t2}
                      onChange={(e) => setT2(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateTempChange} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {resultTempChange !== null && (
                  <>
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                      <CardHeader>
                        <CardTitle className="text-xl">Result</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-primary">
                          {resultTempChange.toFixed(3)} J/K
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Entropy Change
                        </p>
                      </CardContent>
                    </Card>
                    
                    <CalculatorSuperCalcCTA config={calculatorCTAs["entropy-change"]} trigger={resultTempChange} />
                  </>
                )}
              </TabsContent>
            </Tabs>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Entropy measures disorder or randomness</p>
                <p>• ΔS {">"} 0 for spontaneous processes</p>
                <p>• Temperature must be in Kelvin</p>
                <p>• 2nd Law: entropy of universe always increases</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
